import { useEffect } from 'react'
import { dataLayerPush } from './thirdparty'

export function pushFormStart(formType) {
  dataLayerPush({
    event: 'form_start',
    form_type: formType,
  })
}

/**
 * Fires form_start whenever `ready` and the section becomes visible.
 * Omit isSectionVisible (or pass true) on standalone pages that are always shown.
 */
export function useFormStartWhenVisible(formType, { isSectionVisible = true, ready = true } = {}) {
  useEffect(() => {
    if (!formType || !ready || !isSectionVisible) {
      return
    }

    pushFormStart(formType)
  }, [formType, ready, isSectionVisible])
}

export function pushFormSubmit(formType) {
  dataLayerPush({
    event: 'form_submit',
    form_type: formType,
  })
}

function getFormFieldRoot(formContainer) {
  const iframe = formContainer.querySelector?.('iframe')
  if (iframe) {
    return iframe.contentDocument || iframe.contentWindow?.document || null
  }
  return formContainer
}

function getFieldName(field) {
  return field.getAttribute('name') || field.getAttribute('data-name') || field.id || ''
}

function fieldHasValue(field) {
  if (field.type === 'checkbox' || field.type === 'radio') {
    return field.checked
  }
  return (field.value ?? '').trim() !== ''
}

function isTrackableField(field) {
  if (!field || !['INPUT', 'TEXTAREA', 'SELECT'].includes(field.tagName)) return false
  if (field.tagName === 'INPUT' && ['hidden', 'submit', 'button', 'reset', 'image'].includes(field.type)) {
    return false
  }
  return true
}

export function attachFormInteractionTracking(formContainer) {
  const root = getFormFieldRoot(formContainer)
  if (!root) return null

  const userEditedFields = new WeakSet()

  const markEdited = (event) => {
    const field = event.target
    if (isTrackableField(field)) userEditedFields.add(field)
  }

  const handler = (event) => {
    const field = event.target
    if (!isTrackableField(field) || !userEditedFields.has(field) || !fieldHasValue(field)) return

    const formStep = getFieldName(field)
    if (!formStep) return

    dataLayerPush({
      event: 'form_interaction',
      form_step: formStep,
    })
  }

  const fields = root.querySelectorAll('input, textarea, select')
  fields.forEach((field) => {
    field.addEventListener('input', markEdited)
    field.addEventListener('change', markEdited)
    field.addEventListener('blur', handler)
  })

  return () => {
    fields.forEach((field) => {
      field.removeEventListener('input', markEdited)
      field.removeEventListener('change', markEdited)
      field.removeEventListener('blur', handler)
    })
  }
}

export function createD365FormLoadHandler(containerRef, interactionCleanupRef) {
  return function handleFormLoad(event) {
    const formEl = event?.detail?.formElement ?? event?.target
    if (!formEl || !containerRef.current?.contains(formEl)) return

    interactionCleanupRef.current?.()
    interactionCleanupRef.current = attachFormInteractionTracking(formEl)
  }
}

export function registerD365FormLoadListeners(handler) {
  const api = typeof window !== 'undefined' ? window.d365mktforms : null
  api?.addEventListener?.('d365mkt-afterformload', handler)
  document.addEventListener('d365mkt-afterformload', handler)

  return () => {
    api?.removeEventListener?.('d365mkt-afterformload', handler)
    document.removeEventListener('d365mkt-afterformload', handler)
  }
}
